import { FC, ReactNode } from "react";

import rulesMarkdown from "../../content/competition-rules.md?raw";

/** The canonical published rules text (the markdown source this component
 * hand-renders). The consent record stores a sha-256 of exactly this string,
 * so the deposit-time agreement is pinned to the version shipped with the
 * app. Keep the JSX below in sync with the markdown file. */
export const COMPETITION_RULES_MARKDOWN = rulesMarkdown;

/** sha-256 (hex) of the canonical rules text, computed in-browser. */
export const hashRules = async (): Promise<string> => {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(rulesMarkdown),
  );
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

const H2: FC<{ children: ReactNode }> = ({ children }) => (
  <h2 className="mb-3 mt-8 border-b border-sr-border-subtle pb-2 text-lg font-semibold text-white first:mt-0">
    {children}
  </h2>
);

const P: FC<{ children: ReactNode }> = ({ children }) => (
  <p className="mb-3 text-sm leading-relaxed text-sr-oat-milk">{children}</p>
);

const B: FC<{ children: ReactNode }> = ({ children }) => (
  <strong className="font-semibold text-white">{children}</strong>
);

const Li: FC<{ children: ReactNode }> = ({ children }) => (
  <li className="mb-1.5 text-sm leading-relaxed text-sr-oat-milk">{children}</li>
);

const Ext: FC<{ href: string; children: ReactNode }> = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    className="text-sr-lemon-muted underline decoration-dotted underline-offset-2 hover:text-white"
  >
    {children}
  </a>
);

const PRIZES: Array<[string, string, string]> = [
  ["Grand Champion", "$1,000", "Highest Final Standing (A.2)"],
  ["2nd Place", "$600", "Second-highest Final Standing"],
  ["3rd Place", "$400", "Third-highest Final Standing"],
  ["Week 1 Champion", "$350", "Highest Week 1 Growth (A.3)"],
  ["Week 2 Champion", "$350", "Highest Week 2 Growth (A.3)"],
  [
    "Rookie of Week 2",
    "$300",
    "Highest Week 2 Growth among accounts entered after day 6",
  ],
  [
    "Comeback Award",
    "$250",
    "Highest Week 2 Growth among accounts in the bottom half of the day-7 leaderboard",
  ],
  [
    "Steady Hand",
    "$400",
    "Highest Final Standing among accounts with Maximum Drawdown (A.4) ≤ 20%",
  ],
  ["Market Maker's Prize", "$300", "Highest Maker Profit (A.5)"],
  ["Iron Stomach", "$250", "Most Positive Days (A.6), min. activity per A.6"],
  [
    "Biggest Swing Trade",
    "$200",
    "Largest Round-Trip Profit (A.7), subject to integrity review",
  ],
  ["Best Community Coverage", "$400", "Judged per 8.2"],
  ["Coverage Runner-Up", "$200", "Judged per 8.2"],
  ["Spirit of the Competition", "$300", "Discretionary, per 8.3"],
];

export const RulesView: FC = () => {
  return (
    <div className="flex w-full max-w-3xl flex-col gap-6 py-8">
      <div className="sr-card p-6 sm:p-8">
        <h1
          className="mb-6 text-2xl font-semibold text-white"
          style={{ fontFamily: "var(--font-display)" }}
        >
          Sugar Rush Trading Competition — Official Rules
        </h1>

        <H2>1. Overview and Sponsor</H2>
        <P>
          The Sugar Rush Trading Competition (the "Competition") is a
          skill-based trading contest conducted on the Gummiworm layer-2 test
          network (the "Testnet"), operated by SundaeSwap Labs, Inc
          ("Sponsor"). Participants trade fictional, valueless tokens on a
          simulated market and are ranked by the published performance metrics
          below. Prizes are awarded solely on the basis of those metrics. No
          element of chance determines any prize.
        </P>

        <H2>2. Key Facts (plain-language summary)</H2>
        <ul className="mb-3 list-disc pl-5">
          <Li>
            Entry requires a deposit of 10 mainnet ADA, which funds your
            Testnet account and remains your property, withdrawable per Section
            5.
          </Li>
          <Li>
            All traded tokens (VAN, CHOC, MINT, FUDGE, SWIRL, SCOOP) are
            fictional, exist only on the Testnet, and have{" "}
            <B>no monetary value</B>, no redemption right, and no existence
            after the Competition.
          </Li>
          <Li>
            The Competition runs August 24th, 2026 13:00 UTC through September
            6th 20:00 UTC (the "Competition Period"), divided into Week 1 and
            Week 2 (boundary: day 7 at 20:00 UTC).
          </Li>
          <Li>
            Over $5,000 in prizes (USD value at Competition start; see Section
            8) across the categories in Section 8.
          </Li>
          <Li>
            Sponsor operates automated market-making and trading systems in the
            markets (Section 6). A fictional in-game news feed publishes items,
            most of which have no effect on prices (Section 7).
          </Li>
          <Li>
            Market parameters are cryptographically committed before launch and
            the commitment inputs are revealed afterward (Section 9), so
            participants can verify no mid-competition tampering occurred.
          </Li>
        </ul>

        <H2>3. Eligibility</H2>
        <P>
          3.1. Open to natural persons aged 18 or older (or the age of majority
          in their jurisdiction, if higher).
        </P>
        <P>3.2. Void where prohibited. Not open to residents of Quebec.</P>
        <P>
          3.3. Employees, contractors, and immediate family/household members
          of Sponsor are not eligible for prizes. They may trade for
          operational purposes; such accounts are excluded from all rankings.
        </P>
        <P>
          3.4. <B>Account Registration.</B> Each participant may register and
          control exactly one Competition account. Registration requires a 10
          ADA deposit; utilize OAuth login via X (formerly Twitter); and have
          only one account per wallet or IP address. Any winner will also need
          to provide additional identity verification and a valid W-9 form for
          anyone winning $600 or more across all prizes. Operating or
          coordinating multiple accounts is grounds for disqualification of all
          associated accounts.
        </P>
        <P>
          3.5. Use of automated trading (bots) through a participant's own
          single account is expressly permitted (and even encouraged).
          Documentation for building such bots is available at{" "}
          <Ext href="https://sugar.rush.preview.sundae.fi/docs">
            https://sugar.rush.preview.sundae.fi/docs
          </Ext>
          .
        </P>

        <H2>4. Entry; No Purchase of Chance</H2>
        <P>
          4.1. To enter, a participant deposits 10 ADA to the published deposit
          address during the entry window (2026-08-24 13:00 UTC through the end
          of the Competition Period). The deposit funds the participant's
          layer-2 account and is credited alongside a standard bundle of
          Competition tokens (the "Starting Bundle"). Every participant
          receives an identical Starting Bundle at the time of entry: 7,000
          VAN, plus 600 VAN worth of each token at market price as of the time
          the deposit settles.
        </P>
        <P>
          4.2. The deposit is not a fee paid to Sponsor. It is required to
          ensure each entrant is a person with a verifiable identity, and to
          reduce the presence of spam accounts. Your ADA deposit remains yours
          while it is held in the Testnet's on-chain deposit contract, and is
          withdrawable at any time as described in Section 5.
        </P>
        <P>
          4.3. Entry is permitted at any time during the Competition Period.
          Prize categories restricted by entry date are identified in Section
          8.
        </P>

        <H2>5. Deposits, Custody, Withdrawal, and Risk</H2>
        <P>
          5.1. Deposited ADA is held in the Competition's on-chain contract on
          the Cardano mainnet. Participants may withdraw their ADA at any time,
          subject to network conditions. Withdrawal during the Competition
          Period closes the account for further participation, but the account
          will remain eligible for contest prizes.
        </P>
        <P>
          5.2. The Testnet hosting this contest is experimental software.
          Participants acknowledge risks including software defects, downtime,
          and delayed withdrawals. Sponsor will maintain an emergency
          withdrawal ("evacuation") mechanism permitting recovery of deposited
          ADA in the event the Testnet is halted.
        </P>
        <P>
          5.3. Competition tokens are not deposits, are not backed by any
          asset, have no value, carry no rights of any kind, and will cease to
          exist after the Competition.
        </P>

        <H2>6. Sponsor-Operated Market Systems (Disclosure)</H2>
        <P>
          6.1. Sponsor operates automated market-making and trading systems
          ("Liquidity Bots") in all Competition markets as part of the contest
          in order to mimic non-testnet market conditions and events. Liquidity
          Bots provide resting orders and trading volume, and in aggregate are
          expected to transfer Competition tokens to participants over the
          Competition Period. To be clear, Liquidity Bots are not competing in
          the contest and not eligible for prizes.
        </P>
        <P>
          6.2. The identities of Liquidity Bot accounts are not disclosed until
          the end of the competition, and inferring their behavior is a
          legitimate part of the Competition.
        </P>
        <P>
          6.3. Liquidity Bot strategy parameters change over the Competition
          Period according to a schedule fixed before launch and included in
          the commitment described in Section 9. New markets may open during
          the Competition Period on a schedule likewise fixed before launch;
          openings are announced to all participants simultaneously.
        </P>

        <H2>7. The News Feed (Disclosure)</H2>
        <P>
          7.1. The Competition includes a fictional news feed about the
          Competition tokens in order to mimic non-testnet market conditions.
          All content is fiction authored before launch; it does not describe
          real companies, persons, or events.
        </P>
        <P>
          7.2. <B>Most news items have no relationship to price movements.</B>{" "}
          Some items precede scheduled market events. Certain items,
          identifiable by their content as scheduled announcements, always
          precede a market event of unspecified direction. Distinguishing
          consequential items from inconsequential ones is a skill element of
          the Competition. The feed never states future prices.
        </P>
        <P>
          7.3. The complete news feed, including all publication times, is
          fixed before launch and included in the commitment described in
          Section 9.
        </P>

        <H2>8. Prizes</H2>
        <P>
          All values USD. Prizes are paid in ADA at the prevailing exchange
          rate on the competition start date. All metrics are computed from
          Testnet ledger data per the definitions in Appendix A, using VAN as
          the unit of account and the valuation method in A.1. Metric formulas
          are final as published.
        </P>
        <div className="mb-4 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                {["Prize", "Amount", "Metric (defined in Appendix A)"].map(
                  (h) => (
                    <th
                      key={h}
                      className="border-b border-sr-border-subtle px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-sr-neutral-500"
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {PRIZES.map(([prize, amount, metric]) => (
                <tr key={prize}>
                  <td className="border-b border-white/[0.04] px-3 py-2 text-white">
                    {prize}
                  </td>
                  <td className="border-b border-white/[0.04] px-3 py-2 text-sr-oat-milk">
                    {amount}
                  </td>
                  <td className="border-b border-white/[0.04] px-3 py-2 text-sr-oat-milk">
                    {metric}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <P>
          8.1. One prize per category per account. A single account may win
          multiple categories. Ties are broken by even split of prize (or
          prizes for Grand, 2nd and 3rd prizes).
        </P>
        <P>
          8.2. Coverage prizes: participants should cover the event in whatever
          way they see fit on X (formerly Twitter), Youtube, Medium, Substack
          or other social media or news sites. Participants may submit one or
          more pieces of coverage to{" "}
          <Ext href="mailto:sugar-rush@sundae.fi">sugar-rush@sundae.fi</Ext>{" "}
          within 7 days of the competition ending to be eligible for a prize.
          Entries will be judged by sponsor-selected judges on engagement,
          voice, and creativity. Manufactured engagement (purchased or
          automated amplification) disqualifies an entry.
        </P>
        <P>
          8.3. Sponsor and prize sponsors reserve the right to award additional
          prizes and recognitions based on recognized objective skills, before,
          during, or after the Competition Period, in addition to covered
          above.
        </P>
        <P>
          8.4. Prize payment requires completion of Sponsor's tax documentation
          requirements within 30 days of notification; unclaimed prizes after
          60 days are forfeited and will be donated to a non-profit of
          Sponsor's choosing. Winners are responsible for applicable taxes.
        </P>

        <H2>9. Fairness Commitments</H2>
        <P>
          9.1. Before the Competition opens, Sponsor will publish cryptographic
          hashes (SHA-256) committing to: (a) the complete market simulation
          configuration, including all scheduled events and parameter changes;
          and (b) the complete news feed.
        </P>
        <P>
          9.2. Within 7 days after the Competition close, Sponsor will publish
          the inputs to those hashes, enabling any party to verify that market
          events and news content were fixed before launch.
        </P>
        <P>
          9.3. Sponsor personnel with access to the commitment inputs are
          prohibited from participating and from disclosing the inputs before
          the reveal.
        </P>

        <H2>10. Prohibited Conduct; Integrity Review</H2>
        <P>
          10.1. Prohibited: (a) operating or coordinating multiple accounts;
          (b) wash trading (defined as trades between accounts under common or
          coordinated control, or trades whose purpose is transferring value
          between accounts or manufacturing metric performance rather than
          genuine price-taking); (c) collusion to manipulate any prize metric;
          (d) exploiting software defects (report them; see 10.4); (e)
          attacking, degrading, or circumventing Competition infrastructure;
          and (f) interfering with other participants' access.
        </P>
        <P>
          10.2. All provisional winners are subject to an integrity review
          before prizes are paid, including counterparty analysis of the trades
          underlying their metrics. Sponsor may disqualify any account, or
          adjust any metric to remove the effect of prohibited conduct, in its
          reasonable discretion, applied consistently.
        </P>
        <P>
          10.3. Sponsor's determinations following integrity review are final.
        </P>
        <P>
          10.4. Responsible disclosure: participants who report a material
          defect rather than exploiting it may be recognized under Section 8.3.
        </P>

        <H2>11. Technical Failures; Modifications; Cancellation</H2>
        <P>
          11.1. If the Testnet suffers a material failure, Sponsor may pause
          the Competition, and where a pause exceeds 24 hours, extend the
          Competition Period by the paused duration. Scoring excludes intervals
          officially declared as outages.
        </P>
        <P>
          11.2. If a defect materially corrupts scoring, Sponsor may cancel
          affected categories and redistribute their prize amounts, or in cases
          of fundamental failure cancel the Competition, in which case deposits
          remain withdrawable per Section 5 and no prizes are awarded.
        </P>
        <P>
          11.3. Sponsor may amend these Rules before the entry window opens.
          Amendments during the Competition Period are limited to corrections
          that do not retroactively change any metric, and will be announced to
          all participants simultaneously.
        </P>

        <H2>12. Publicity and Data</H2>
        <P>
          12.1. Account performance data (pseudonymous account identifiers,
          rankings, trade statistics) will be displayed publicly during and
          after the Competition.
        </P>
        <P>
          12.2. Winners consent to publication of their handle/pseudonym and
          winning metrics. Publication of legal names requires separate
          consent.
        </P>
        <P>
          12.3. Personal data collected for identity verification and prize
          payment is processed per{" "}
          <Ext href="https://sundae.fi/privacy">https://sundae.fi/privacy</Ext>
          .
        </P>

        <H2>13. General</H2>
        <P>13.1. These Rules are governed by the laws of Wyoming.</P>
        <P>
          13.2. Agreement to Arbitrate. This Section 13.2 evidences a
          transaction involving interstate commerce and its enforceability is
          governed by the Federal Arbitration Act, 9 U.S.C. §§ 1 et seq. (the
          "FAA"). Sponsor and each participant agree that any dispute, claim,
          or controversy arising out of or relating to these Rules, the
          Competition, or the breach, termination, enforcement, interpretation,
          or validity of these Rules (each, a "Dispute") will be resolved
          solely by final and binding arbitration administered by JAMS pursuant
          to its Streamlined Arbitration Rules and Procedures then in effect
          (the "JAMS Rules"), rather than in court, except as set forth in
          subsection (c) below.
        </P>
        <P>
          (a) Seat; Arbitrator's Authority. The arbitration will be conducted
          by video conference call (or other manner as mutually agreed) before
          a single arbitrator in accordance with the JAMS Rules. The
          arbitrator, and not any court, has exclusive authority to resolve any
          Dispute relating to the interpretation, applicability,
          enforceability, or formation of this Section 13.2, including any
          claim that all or part of it is void or voidable.
        </P>
        <P>
          (b) Class Action and Jury Trial Waiver. SPONSOR AND EACH PARTICIPANT
          WAIVE ANY RIGHT TO A TRIAL BY JURY. ANY ARBITRATION OR OTHER
          PROCEEDING UNDER THIS SECTION 13.2 WILL BE CONDUCTED ONLY ON AN
          INDIVIDUAL BASIS AND NOT AS A CLASS, COLLECTIVE, CONSOLIDATED, OR
          REPRESENTATIVE ACTION, AND THE ARBITRATOR MAY NOT CONSOLIDATE MORE
          THAN ONE PERSON'S CLAIMS. If this class action and jury trial waiver
          is found unenforceable as to a particular Dispute, that Dispute (and
          only that Dispute) will be resolved in a court of competent
          jurisdiction seated in Laramie County, Wyoming, and the remainder of
          this Section 13.2 will otherwise remain in full force and effect.
        </P>
        <P>
          (c) Injunctive Relief Carve-Out. Notwithstanding subsection (b),
          either party may bring an individual action in a court of competent
          jurisdiction seated in Laramie County, Wyoming, to seek temporary,
          preliminary, or permanent injunctive or other equitable relief to
          prevent actual or threatened infringement, misappropriation, or
          violation of a party's intellectual property rights, or unauthorized
          access to or interference with the Competition, the Testnet, or
          Sponsor's systems, pending resolution of the underlying Dispute in
          arbitration.
        </P>
        <P>
          (d) Confidentiality. The parties will treat the arbitration
          proceeding and any award as confidential, except as necessary to
          prepare for or conduct the arbitration, to enforce or challenge an
          award, or as required by applicable law.
        </P>
        <P>
          (e) Severability. If any provision of this Section 13.2 (other than
          the class action and jury trial waiver in subsection (b), which is
          addressed separately above) is found unenforceable, that provision
          will be severed and the remainder of this Section 13.2 will remain in
          full force and effect.
        </P>
        <P>
          13.3. The Competition is not sponsored or endorsed by, and is not
          affiliated with, the Cardano Foundation, Input Output, or any token
          project referenced by any prize sponsor, except as expressly stated.
        </P>
        <P>
          13.4. Nothing in the Competition is investment advice or an offer of
          securities. Competition tokens are game pieces with no value.
        </P>
        <P>13.5. Limitation of Liability; Disclaimer of Warranties.</P>
        <P>
          (a) Disclaimer. The Competition, the Testnet, and all related
          software, systems, and services are provided "as is" and "as
          available," without warranties of any kind, whether express, implied,
          or statutory, including any implied warranties of merchantability,
          fitness for a particular purpose, title, or non-infringement, and any
          warranty arising from course of dealing or usage of trade. Sponsor
          does not warrant that the Testnet or the Competition will be
          uninterrupted, error-free, or secure.
        </P>
        <P>
          (b) Exclusion of Certain Damages. To the maximum extent permitted by
          applicable law, in no event will Sponsor, its affiliates, or their
          respective officers, directors, employees, or agents be liable for
          any indirect, incidental, special, consequential, exemplary, or
          punitive damages, or for any loss of profits, revenue, data, or
          goodwill, arising out of or relating to the Competition, these Rules,
          or a participant's use of or inability to use the Testnet, even if
          advised of the possibility of such damages.
        </P>
        <P>
          (c) Liability Cap. To the maximum extent permitted by applicable law,
          Sponsor's total aggregate liability to any participant arising out of
          or relating to the Competition or these Rules, for any cause of
          action and regardless of the theory of liability, will not exceed the
          greater of (i) the amount of ADA that participant deposited under
          Section 4.1, valued in USD at the time of deposit, or (ii) $100.
        </P>
        <P>
          (d) Carve-Outs. Nothing in this Section 13.5 limits or excludes
          Sponsor's liability for (i) fraud or fraudulent misrepresentation,
          (ii) gross negligence or willful misconduct, (iii) death or personal
          injury caused by Sponsor's negligence, or (iv) any liability that
          cannot be limited or excluded under applicable law. Nothing in this
          Section 13.5 limits a participant's right to withdraw their own
          deposited ADA under Section 5.
        </P>
        <P>
          (e) Basis of the Bargain. Participants acknowledge that Sponsor has
          set the terms of these Rules, including the deposit amount in Section
          4.1, in reliance on the limitations of liability and disclaimers in
          this Section 13.5, and that those limitations are an essential basis
          of the bargain between Sponsor and each participant.
        </P>

        <hr className="my-6 border-sr-border-subtle" />

        <H2>Appendix A — Metric Definitions (computable from ledger data; final)</H2>
        <ul className="mb-3 list-disc pl-5">
          <Li>
            <B>A.1. Valuation.</B> An account's Value at a time is its VAN
            balance plus, for each other token held, the proceeds of simulated
            liquidation of that balance against the resting order book at that
            time (walking the book; unfillable remainder valued at zero).
            "Daily Close" = account Value at 20:00 UTC each Competition day.
            Day numbering: day 1 ends at the first 20:00 UTC after open.
          </Li>
          <Li>
            <B>A.2. Final Standing.</B> The time-weighted average of the
            account's Value (per A.1, sampled at regular published intervals)
            over the final 6 hours of the Competition Period.
          </Li>
          <Li>
            <B>A.3. Week Growth.</B> (Value at week end − Value at week start)
            ÷ Value at week start, using Daily Closes; for accounts entering
            mid-week, entry Value substitutes for week-start Value.
          </Li>
          <Li>
            <B>A.4. Maximum Drawdown.</B> Over the account's Daily Closes: the
            maximum of (peak-so-far − subsequent close) ÷ peak-so-far.
          </Li>
          <Li>
            <B>A.5. Maker Profit.</B> Realized profit attributable to fills of
            the account's resting orders (fills where the account was not the
            aggressing order), marked per A.1.
          </Li>
          <Li>
            <B>A.6. Positive Days.</B> Count of Competition days with (close −
            prior close) &gt; 0, among accounts with at least 3 executed fills
            on each of at least 10 distinct Competition days.
          </Li>
          <Li>
            <B>A.7. Round-Trip Profit.</B> For a single market: the realized
            profit of one entry fill-set and one exit fill-set (buy→sell or
            sell→buy) with notional ≥ 1000 VAN, computed trade-by-trade; the
            "largest" is the highest such profit by any account.
          </Li>
        </ul>
      </div>
    </div>
  );
};
